// Copyright ©️ 2026 eVolve MEP, LLC

import { type Ref, useState, useMemo, useLayoutEffect } from 'react';

import type { MantineStyleProps, FlexProps } from '@mantine/core';

import type { WithRequired, StrongOmit } from 'helpers/helper-types';

function getInlineSize(entry: ResizeObserverEntry | undefined): number {
  if (!entry) return 0;
  // Prefer contentBoxSize if available (better accounts for box-sizing)
  const box = (
    entry as StrongOmit<ResizeObserverEntry, 'contentBoxSize'> & {
      // `contentBoxSize` is typed as `ResizeObserverSize[]` but may not be an array in older browsers
      contentBoxSize?: ResizeObserverSize | ResizeObserverSize[];
    }
  ).contentBoxSize;
  if (box) {
    const size = Array.isArray(box) ? box[0] : box;
    if (typeof size?.inlineSize === 'number') return size.inlineSize;
  }
  // Fallback widely supported
  return entry.contentRect.width;
}

/**
 * Used to generate a uniform flex layout with a minimum and maximum width on each item.
 * Spread the returned `containerProps` and `itemProps` onto their respective elements.
 *
 * Or, set `setCssProps` to true to use the following CSS variables on your container and items.
 *
 * On the container, add the returned `ref` and set
 *
 *     gap: var(--container-gap)
 *
 * On the items, set
 *
 *     flex: 0 0 var(--card-w)
 *     min-width: var(--card-miw)
 *     max-width: var(--card-maw)
 *
 * @param minWidth minimum width of item
 * @param maxWidth maximum width of item
 * @param enabled if false, will not apply the latest layout
 * @param gap gap between items
 * @param setCssProps if true, will set CSS variables for use on the container and items
 * @returns a object with:
 *
 * - `containerProps` to spread onto your container
 * - `itemProps` to spread onto your items
 * - a `ref` to set on your container if using `setCssProps`
 */
export function useUniformFlex(minWidth: number, maxWidth: number, enabled = true, gap = 16, setCssProps = false) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const ref = useMemo<Ref<HTMLDivElement>>(() => setContainer, []);

  const [itemProps, setItemProps] = useState<WithRequired<Pick<MantineStyleProps, 'flex' | 'miw' | 'maw'>>>();
  const containerProps = useMemo<WithRequired<Pick<FlexProps, 'display' | 'wrap' | 'gap'>> & { ref: typeof ref }>(
    () => ({
      ref,
      display: 'flex',
      wrap: 'wrap',
      gap: setCssProps ? 'var(--container-gap)' : gap,
    }),
    [gap, setCssProps, ref],
  );

  useLayoutEffect(() => {
    if (!container) return undefined;

    const itemWidth = (containerWidth: number, cols: number) => (containerWidth - (cols - 1) * gap) / cols;

    const ro = new ResizeObserver(([entry]) => {
      const width = getInlineSize(entry);
      if (width <= 0) return;

      // Start with as many columns such that width >= min
      let cols = Math.max(1, Math.floor((width + gap) / (minWidth + gap)));

      // If that yields items wider than max, add columns until in range
      while (itemWidth(width, cols) > maxWidth) cols += 1;

      const cardW = itemWidth(width, cols);

      if (enabled) {
        if (setCssProps) {
          container.style.setProperty('--container-gap', `${gap}px`);
          container.style.setProperty('--card-w', `${cardW}px`);
          container.style.setProperty('--card-miw', `${minWidth}px`);
          container.style.setProperty('--card-maw', `${maxWidth}px`);
        }
        setItemProps({
          flex: setCssProps ? `0 0 var(--card-w)` : `0 0 ${cardW}px`,
          miw: setCssProps ? `var(--card-miw)` : minWidth,
          maw: setCssProps ? `var(--card-maw)` : maxWidth,
        });
      }
    });

    ro.observe(container);
    return () => ro.disconnect();
  }, [minWidth, maxWidth, gap, setCssProps, container, enabled]);

  return useMemo(
    () => ({
      ref,
      containerProps,
      itemProps,
    }),
    [ref, containerProps, itemProps],
  );
}
